import { html, LitElement, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import SlotController from "@/common/controllers/slot";
import { EkoElement } from "@/utilities/element";
import style from "./modal.css?inline";
import "@/components/icon";
import "@/components/featured-icon";
import "@/components/visually-hidden";
import "./modal-icon";
import "./modal-title";
import "./modal-subtitle";

@customElement("eko-modal")
class Modal extends EkoElement {
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private footerSlot = new SlotController(this, "footer");
  private iconSlot = new SlotController(this, "icon");
  private subtitleSlot = new SlotController(this, "subtitle");
  private titleSlot = new SlotController(this, "title");

  @property({ reflect: true, type: Boolean }) divider = false;
  @property({ reflect: true }) layout?: "default" | "centered" | "compact" = "default";
  @property({ reflect: true, type: Boolean }) open = false;
  @property({ reflect: true, type: Boolean }) scrollable = false;
  @property({ reflect: true }) size?: "xs" | "sm" | "md" | "lg" | "xl" = "md";
  @property({ reflect: true }) variant: "default" | "info" = "default";

  private handleClose() {
    const allowed = this.dispatchEvent(new Event("cancel", { cancelable: true }));

    if (allowed) {
      this.hide();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.setAttribute("role", "dialog");
  }

  hide() {
    this.open = false;

    this.dispatchEvent(new Event("close"));
  }

  render() {
    return html`<eko-overlay @close=${this.handleClose} part="overlay">
      <div class="eko-modal" tabindex="0" part="root">
        <div class="eko-modal__header" part="header">
          <div class="eko-modal__header-content" part="header-content">
            <slot name=${this.iconSlot.name}></slot>
            <div class="eko-modal__header-inner-content" part="header-inner-content">
              <slot name=${this.titleSlot.name}></slot>
              <slot name=${this.subtitleSlot.name}></slot>
            </div>
          </div>
          <eko-button
            @click=${this.handleClose}
            class="eko-modal__header-close"
            part="close"
            square
            variant="plain">
            <eko-icon name="x-close" part="close-icon"></eko-icon>
            <eko-visually-hidden>Close</eko-visually-hidden>
          </eko-button>
        </div>
        <div class="eko-modal__body" part="body">
          <slot></slot>
        </div>
        <slot name=${this.footerSlot.name}></slot>
      </div>
    </eko-overlay>`;
  }

  show() {
    this.open = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-modal": Modal;
  }
}

export default Modal;
